import React, {useCallback} from 'react';
import type {DragEndEvent, DragStartEvent} from '@dnd-kit/core';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {SortableContext, useSortable} from '@dnd-kit/sortable';
import {assert} from 'assert-ts';
import type {SelectActionType} from 'feature/editThesaurus/types';
import type {ShowThesaurusCode} from 'schemaDefinition/types';
import type {NodeAndAncestors, ThesaurusNode} from 'services/thesaurus/types';
import {ColorPalette} from 'theme';
import {FlexBox, Layout} from 'components/layout';
import {Text} from 'components/text';
import type {ThesaurusValueProps} from './ThesaurusValue';
import {ThesaurusValue} from './ThesaurusValue';

type Props = {
  category: ThesaurusNode;
  nodes: NodeAndAncestors[];
  readonly?: boolean;
  showCode?: ShowThesaurusCode;
  highlight: string[];
  highlightType?: SelectActionType;
  onDelete?: (nodeId: string) => void;
  onSelect?: (node: ThesaurusNode) => void;
  onMove?: (oldIndex: number, newIndex: number) => void;
};

const labelSx = {
  mb: 1,
  textTransform: 'none',
  color: ColorPalette.textSecondary,
} as const;

export const ThesaurusValueCategory: React.FC<Props> = ({
  category,
  nodes,
  readonly,
  showCode,
  highlight,
  highlightType,
  onDelete,
  onSelect,
  onMove,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 0,
        tolerance: 50,
      },
    }),
  );

  const [activdDndNode, setActiveDndId] = React.useState<ThesaurusNode>();

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const node = nodes.find(n => n.node.id === event.active.id);
      setActiveDndId(assert(node, 'DragStart: node not found').node);
    },
    [nodes],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const {active, over} = event;

      setActiveDndId(undefined);

      if (!active || !over || active.id === over.id) {
        return;
      }

      if (onMove) {
        const fromIndex = nodes.findIndex(n => n.node.id === active.id);
        const toIndex = nodes.findIndex(n => n.node.id === over.id);

        onMove(fromIndex, toIndex);
      }
    },
    [nodes, onMove],
  );

  return readonly ? (
    <Layout adjustLeft pt={2}>
      <Text variant="label" sx={labelSx}>
        {category.label}
      </Text>
      <FlexBox gap={1}>
        {nodes.map(n => (
          <ThesaurusValue
            key={n.node.id}
            node={n.node}
            showCode={showCode}
            highlight={false}
            highlightType={highlightType}
            canMove={false}
          />
        ))}
      </FlexBox>
    </Layout>
  ) : (
    <Layout adjustLeft pt={2}>
      <Text variant="label" sx={labelSx}>
        {category.label}
      </Text>

      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}>
        <SortableContext items={nodes.map(n => n.node)}>
          {nodes.map(n => {
            const highlightValue = highlight.includes(n.node.id);
            return (
              <SortableItem
                key={n.node.id}
                isActive={activdDndNode?.id === n.node.id}
                id={n.node.id}
                node={n.node}
                showCode={showCode}
                highlight={highlightValue}
                highlightType={highlightType}
                canMove={nodes.length > 1 && !!onMove}
                onDelete={onDelete}
                onSelect={onSelect}
              />
            );
          })}
        </SortableContext>
        <DragOverlay>
          {activdDndNode ? (
            <Item
              node={activdDndNode}
              showCode={showCode}
              highlight={highlight.includes(activdDndNode.id)}
              highlightType={highlightType}
              canMove={nodes.length > 1 && !!onMove}
              onDelete={onDelete}
              onSelect={onSelect}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </Layout>
  );
};

type SortableItemProps = ThesaurusValueProps & {
  isActive?: boolean;
  id: string;
};

const SortableItem: React.FC<SortableItemProps> = ({
  isActive,
  id,
  ...props
}) => {
  const {attributes, listeners, setNodeRef, transform, transition} =
    useSortable({id});

  const style = {
    // Do this rather than CSS.Transform.toString(transform) to
    // avoid stretching/shrinking items while dragging
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {isActive ? (
        <FlexBox hidden>
          <Item {...props} dragListeners={listeners} />
        </FlexBox>
      ) : (
        <Item {...props} dragListeners={listeners} />
      )}
    </div>
  );
};

const Item: React.FC<ThesaurusValueProps> = props => (
  <FlexBox py={0.5}>
    <ThesaurusValue {...props} />
  </FlexBox>
);
